import { useRef } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Link,
  TextField,
  Typography,
} from '@mui/material'
import { useLetterboxdIntegration } from './hooks/useLetterboxdIntegration'

export function LetterboxdIntegrationCard() {
  const {
    status,
    loading,
    busy,
    message,
    setMessage,
    username,
    setUsername,
    saveUsername,
    syncRatings,
    importZip,
    disconnect,
  } = useLetterboxdIntegration()
  const fileRef = useRef<HTMLInputElement>(null)

  return (
    <Card sx={{ backgroundColor: 'background.default', borderRadius: 2, height: '100%' }}>
      <CardContent>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <Typography variant="h6">Letterboxd</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Sync your Letterboxd ratings to improve your recommendations. Ratings for films in your
          library are applied automatically.
        </Typography>

        {message && (
          <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
            {message.text}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box display="flex" gap={1} alignItems="flex-start" mb={1}>
              <TextField
                label="Letterboxd username"
                size="small"
                fullWidth
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                disabled={busy}
              />
              <Button
                variant="contained"
                onClick={() => void saveUsername()}
                disabled={busy}
                size="small"
                sx={{ mt: 0.25 }}
              >
                Save
              </Button>
            </Box>

            {status?.connected && (
              <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1, my: 2 }}>
                <Typography variant="body2" fontWeight={500}>
                  Connected as {status.username}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {status.ratingCount} ratings imported
                  {status.syncedAt
                    ? ` · last synced ${new Date(status.syncedAt).toLocaleString()}`
                    : ''}
                </Typography>
              </Box>
            )}

            <Box display="flex" gap={1} flexWrap="wrap" mb={2} mt={status?.connected ? 0 : 2}>
              <Button
                variant="contained"
                size="small"
                onClick={() => void syncRatings()}
                disabled={busy || !status?.connected}
              >
                {busy ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                Sync now
              </Button>
              {status?.connected && (
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={() => void disconnect()}
                  disabled={busy}
                >
                  Disconnect
                </Button>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" gutterBottom>
              Import full history
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              The username sync only sees recent activity. To import all your ratings at once, export
              your data at{' '}
              <Link href="https://letterboxd.com/settings/data/" target="_blank" rel="noopener">
                letterboxd.com/settings/data
              </Link>{' '}
              and upload the .zip below.
            </Typography>
            <input
              ref={fileRef}
              type="file"
              accept=".zip"
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0]
                if (f) void importZip(f)
                e.target.value = ''
              }}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={() => fileRef.current?.click()}
              disabled={busy}
            >
              {busy ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
              Upload Letterboxd export (.zip)
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  )
}
